import { For, If, location, onMount, type JSX } from 'cdui-js';
import { goBackByBrowser } from '../../../../lib/browser';
import i18n from '../../../../i18n';
import { getErrorFieldLabel } from '../open-progress/errorKeyNormalize';
import { ensureOpenProgressAppBackHandler, pState } from '../open-progress/store';
import { finishOpenAccount } from '../open-progress/showSubmitSuccessModal';
import { Reviewing } from '../open-progress/on-line/transfer/Reviewing';
import { LastNextBarPortal } from '../open-progress/components/LastNextBtns';
import { ResultFailIcon, ResultSuccessIcon, ResultWarningIcon } from './icons';
import './style.css';

export type ResultStatus = 'success' | 'fail' | 'warning';

export type ResultDescriptionItem = {
  label: string;
  value: string;
};

export type AccountResultProps = {
  class?: string;
  /** 2.1 成功绿勾 / 失败红叉 / 警告红感叹号 */
  status?: ResultStatus;
  /** 2.2 */
  title?: string;
  /** 2.3 纯文案，空则不渲染 */
  description?: string;
  /** 2.3 灰底错误列表（与 description 可同时存在） */
  descriptionItems?: ResultDescriptionItem[];
  /** 2.4 自定义内容，默认空 */
  children?: JSX.Element;
  contentRender?: () => JSX.Element;
  /** 2.5 默认「确定」 */
  buttonText?: string;
  /** 不传则返回上一页 */
  onButtonClick?: () => void;
  showButton?: boolean;
};

const RESULT_STATUSES: ResultStatus[] = ['success', 'fail', 'warning'];

const renderStatusIcon = (status: ResultStatus) => {
  if (status === 'fail') return <ResultFailIcon />;
  if (status === 'warning') return <ResultWarningIcon />;
  return <ResultSuccessIcon />;
};

export const AccountResult = (props: AccountResultProps) => {
  const status = () => props.status ?? 'success';
  const hasExtra = () => props.contentRender != null || props.children != null;
  const handleClick = () => {
    if (props.onButtonClick) {
      props.onButtonClick();
      return;
    }
    goBackByBrowser();
  };

  return (
    <div class={`account-result flex-column bg-c ${props.class ?? ''}`.trim()}>
      <div class="account-result-header flex-column align-center padding-m-x">
        {renderStatusIcon(status())}
        <If when={!!props.title}>
          <div class="color-D font-s-L font-bold margin-y-S text-center" style={{'margin-top': '20px'}}>{props.title}</div>
        </If>
        <If when={!!props.description}>
          <div class="color font-s-S margin-t-XS text-center">{props.description}</div>
        </If>
      </div>

      <If when={(props.descriptionItems?.length ?? 0) > 0}>
        <div class="margin-t padding-m-x">
          <div class="bg-c-XD round-XL padding-m">
            <For each={props.descriptionItems || []}>
              {(item) => (
                <div class="padding-y-XXS flex justify-space-between gap-12px align-start">
                  <span class="color font-s-S">{item.label}</span>
                  <span class="color-error font-s-S text-right">{item.value}</span>
                </div>
              )}
            </For>
          </div>
        </div>
      </If>

      <If when={hasExtra()}>
        <div class="account-result-content padding-m-x">
          {props.contentRender ? props.contentRender() : props.children}
        </div>
      </If>

      <If when={props.showButton !== false}>
        <div class="account-result-foot-space" />
        <div class="fixed position-b position-l width-100 bg-c page-foot padding-m-x" style={{ 'z-index': 150 }}>
          <button class="confirm-btn btn-rounded width-100 margin-b-S" onclick={handleClick}>
            {props.buttonText || i18n.OpenProgress.Common.Confirm}
          </button>
        </div>
      </If>
    </div>
  );
};

/** 开户门禁结果：依赖 open-progress store 已 setState 的资料 */
const OpenApplyResultPage = () => {
  const E = i18n.OpenProgress.Entry;
  const type = String(location.query.type || pState.status || '');

  // 冷启动进结果页时也要挂 App 返回（与 open-progress 共用 handler）
  onMount(() => {
    if (type === 'SUBMITTED' || type === 'APPROVING') {
      ensureOpenProgressAppBackHandler();
    }
  });

  if (type === 'BACK') {
    const descriptionItems: ResultDescriptionItem[] = (pState.errorValues || []).map(
      (item: { key?: string; message?: string }, index: number) => ({
        label: getErrorFieldLabel(item.key) || String(index + 1),
        value: item.message || '',
      })
    );

    return (
      <AccountResult
        status="warning"
        title={E.ResultBackTitle}
        description={E.ResultBackDesc}
        descriptionItems={descriptionItems}
        buttonText={E.ResultGoEdit}
        onButtonClick={() => {
          // 与 App mode=editRefused 对齐：重新 init，由 query 开启纠错模式
          pState.loading = true;
          location.routeTo('/open-progress?mode=editRefused');
        }}
      />
    );
  }

  if (type === 'SUBMITTED' || type === 'APPROVING') {
    // 提交中 / 审批中：复用开户进度成功页（Reviewing 时间线），挂底栏 Portal 以固定「完成」
    return (
      <>
        <Reviewing onDone={() => finishOpenAccount()} />
        <LastNextBarPortal />
      </>
    );
  }

  return (
    <AccountResult
      status="success"
      title={E.ResultSubmittedTitle}
      description={E.ResultSubmittedDesc}
      buttonText={i18n.OpenProgress.Common.Done}
      onButtonClick={() => {
        location.routeTo('/account');
      }}
    />
  );
};

/** 独立路由：用 query 配置简单结果（status / title / description / buttonText / redirect） */
const AccountResultPage = () => {
  if (String(location.query.scene || '') === 'open-apply') {
    return <OpenApplyResultPage />;
  }

  const statusRaw = String(location.query.status || '');
  const status = RESULT_STATUSES.includes(statusRaw as ResultStatus)
    ? (statusRaw as ResultStatus)
    : 'success';
  const redirect = String(location.query.redirect || '');

  return (
    <AccountResult
      status={status}
      title={String(location.query.title || '')}
      description={String(location.query.description || '')}
      buttonText={String(location.query.buttonText || '') || undefined}
      onButtonClick={() => {
        if (redirect) {
          location.routeTo(redirect);
          return;
        }
        goBackByBrowser();
      }}
    />
  );
};

export default AccountResultPage;
